import lodash from 'lodash'
import { unref } from 'vue-demi'
import { getLocations } from '@/api/locations/request'
import { getSameBuildingUsersByPermission } from '@/api/users/request'
import router from '@/router'
import store from '@/store'
import i18n from '@/utils/i18n'
import notification from '@/utils/notification'
import * as regex from '@/utils/regex'
import { emailSeparator } from './consts'
import { aclHelper } from '@/utils/aclHelper'
import { contactTypes } from '../users/consts'

export async function getSiteInfo (code) {
  let siteInfo = {}
  try {
    const resp = await getLocations({ code })
    if (resp[0]) {
      siteInfo = resp[0]
    }
  } catch (err) {
    notification.handleResponseError(err, 'Get Site Info')
  }
  siteInfo.note = siteInfo.note || 'The Site'
  siteInfo.subsidiary = siteInfo.subsidiary || 'Hongkong Land Property Co Ltd'

  return siteInfo
}

export function getSubsidiaryTextArray (subsidiaryString) {
  const dom = new DOMParser().parseFromString(`<div>${subsidiaryString}</div>`, 'text/html')
  return dom.body.innerText.split('\n').map(str => str && str.trim()).filter(str => str)
}

export function getLink (name, id, hostname) {
  const link = router.resolve({
    name,
    params: { id },
  })
  if (hostname) {
    hostname = hostname.replace(/[a-zA-z]+:\/\//, '')
  }
  const linkResult = hostname ? location.protocol + '//' + hostname.concat(`${link.href}`) : location.origin.concat(`${link.href}`)
  return linkResult
}

// to all extra link, for vendor and tenant
export function getPublicLink (name, id) {
  const vendorEntry = store.getters['settings/vendorEntry']
  return getLink(name, id, vendorEntry)
}

export function getLinkToTab (name, params = {}, query = {}, hostname) {
  const link = router.resolve({
    name,
    params,
    query,
  })
  if (hostname) {
    hostname = hostname.replace(/[a-zA-z]+:\/\//, '')
  }
  return hostname ? location.protocol + '//' + hostname.concat(`${link.href}`) : location.origin.concat(`${link.href}`)
}

export function getVendorHomeLink () {
  const vendorEntry = store.getters['settings/vendorEntry']
  return getLink('vendorHome', null, vendorEntry)
}

export function getTenantDashboardLink () {
  const engineerEntry = store.getters['settings/engineerEntry']
  return getLink('tenantDashboard', null, engineerEntry)
}

/**
 * Build email payload for the sending email API
 *
 * @export
 * @param {String[]} [emails=[]]
 * @param {String|String[]} toEmail
 * @param {String} template
 * @param {Object} metadata
 * @param {String|String[]} metadata.cc
 * @param {String|String[]} metadata.bcc
 * @param {String} notificationPrefix
 */
export function buildEmail (emails = [], toEmail, template, metadata, notificationPrefix) {
  if (toEmail) {
    const mailRequest = {
      to: toEmail,
      template: template,
      metadata: JSON.stringify({ ...metadata }),
    }
    emails.push({ mailRequest, notificationPrefix })
  }
}

export async function getBuildingUsersEmails (buildingId, tenancyType, perms) {
  const emails = await getUserEmails(buildingId, tenancyType, perms)
  return emails
}

export async function getUserEmails (buildingId, tenancyType, perms) {
  return getUserEmailsByPermission({buildingId, tenancyType, permTypes: perms})
}

export async function getUserEmailsByPermission ({
  buildingId,
  tenancyId,
  tenancyType,
  permTypes,
  roleName,
}) {
  const sameBuildingUsers = await getSameBuildingUsersByPermission({
    buildingId,
    tenancyId,
    tenancyType,
    includeSelf: true,
    permTypes,
    roleName,
  })
  let emails = []
  sameBuildingUsers.forEach(item => {
    if (item.email) {
      emails.push(item.email)
    } else {
      emails.push(item.username)
    }
  })
  return emails
}

/**
 * @param {string|string[]} to
 * @param {string|string[]} cc
 * @param {string|string[]} bcc
 */
export function processDuplicateEmails (to, cc, bcc) {
  to = Array.isArray(to) ? to : []
  cc = Array.isArray(cc) ? cc : []
  bcc = Array.isArray(bcc) ? bcc : []
  lodash.remove(bcc, item => cc.includes(item))
  lodash.remove(bcc, item => to.includes(item))
  lodash.remove(cc, item => to.includes(item))
}

export function splitEmails (emailStr, separator = emailSeparator) {
  return (emailStr || '').split(separator)
    .map(email => email.trim())
    .filter(email => email)
}

export function validateEmails (emailStr) {
  let emailTips = i18n.t('hint.inputTip.email')
  let result = splitEmails(emailStr)
    .every(mail => mail === '' || regex.email.test(mail))
  if (
    typeof emailStr === 'string' &&
    (emailStr.includes(',') || emailStr.includes(':'))
  ) {
    emailTips = i18n.t('hint.inputTip.emailSemiColon')
    result = false
  }
  return { result, emailTips }
}

/**
 * @returns {string}
*/
export function concatEmails (emails, separator = emailSeparator) {
  return emails.filter(Boolean).join(separator)
}

/**
 * @param {object} option
 * @param {string|string[]} option.to
 * @param {string|string[]} option.cc
 * @param {string|string[]} option.bcc
 */
export function processDuplicateReceivers ({ to, cc, bcc }) {
  const [toRef, ccRef, bccRef] = [to, cc, bcc].map(value => {
    const item = unref(value)
    const result = Array.isArray(item) ? item : splitEmails(item, emailSeparator)
    return lodash.uniq(result)
  })
  lodash.remove(bccRef, email => ccRef.includes(email) || toRef.includes(email))
  lodash.remove(ccRef, email => toRef.includes(email))
  return { to: concatEmails(toRef), cc: concatEmails(ccRef), bcc: concatEmails(bccRef) }
}

export function getVendorSubEmailsByRight (vendorSubAccounts, permTypes) {
  const result = vendorSubAccounts.filter(user => {
    return aclHelper.checkCustomPermission(user.perms, permTypes) && user.enabled
  }).map(user => {
    const filteredContact = (user.contacts || [])
      .find(contact => contact.type === contactTypes.EMAIL && contact.priority)
    return filteredContact ? filteredContact.value : null
  }).filter(Boolean)
  return result
}
